import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  CTAButton,
  H2,
  HeroH1,
  InlineLink,
  P,
  SeoPageShell,
} from "./seo/SeoPageLayout";

const WhyILookLikeTheCrazyOne = () => {
  return (
    <SeoPageShell>
      <SEO
        title="Why Do I Always Look Like the Crazy One? (Reactive Abuse)"
        description="You didn't start out as the angry one — but you're the one who ends up apologizing. Why the person who finally reacts looks like the problem, and why that's not a verdict on who you are."
        path="/why-do-i-look-like-the-crazy-one"
      />

      <HeroH1>Why Your Reaction Becomes The Evidence</HeroH1>

      <P>
        You didn't start out as the angry one. But somehow, after months of it, you're the one
        who yelled — and that's the part everyone remembers. Here's why the person who finally
        reacts ends up looking like the problem, and why that's not a verdict on who you are.
      </P>
      <P>
        If you've been here for a while, you already know the cost.{" "}
        <InlineLink to="/why-do-i-feel-exhausted-after-conversations">
          why these conversations leave you so exhausted →
        </InlineLink>
      </P>

      <H2>The reaction is the only part anyone sees</H2>
      <P>
        The provocation is quiet. Deniable. A tone, a look, a question that never gets answered,
        the same small thing for the hundredth time. Your reaction is loud — a raised voice, a
        slammed door, a text you'd take back. So when anyone looks at the situation (including,
        sometimes, you), the loud part is the only part that's visible. The months of quiet
        pressure leave no mark. The thirty seconds you lost it are the whole record.
      </P>

      <H2>Why you start to believe it too</H2>
      <P>
        This is the part that does the real damage: after enough cycles, you start agreeing that
        you're the problem. You were the one who shouted. You apologized. You resolved to stay
        calmer next time — and then it happened again, because the pressure was never about your
        composure. When the only evidence on the table is your reaction, you become your own
        prosecutor. That's not weakness. That's what being slowly, deniably provoked does to a
        person's read on themselves.
      </P>

      <CTAButton />

      <H2>A reaction is not a character</H2>
      <P>
        One reaction, pulled out of the sequence that produced it, tells you almost nothing. A
        calm person can be made to snap. The question that actually matters isn't "did I react
        badly" — it's "what was happening, repeatedly, that I kept reacting to." That shift,
        from judging the moment to seeing the pattern, is where people stop carrying a guilt
        that was never theirs to begin with.
      </P>

      <H2>Seeing the whole sequence</H2>
      <P>
        The reason this is so hard to explain to anyone — a friend, a therapist, a court — is
        that you can only ever show them the loud moment. The pattern that caused it lives
        across a hundred small exchanges that each look like nothing. Laying the whole sequence
        out, and naming the mechanism underneath it, is how the invisible part finally becomes
        visible —{" "}
        <InlineLink to="/ai-detect-narcissistic-abuse">
          this is what forensic AI detection is designed to reveal
        </InlineLink>
        .
      </P>
      <P>
        If you want the definitional version of this dynamic —{" "}
        <InlineLink to="/reactive-abuse-examples">
          what reactive abuse looks like, with real examples →
        </InlineLink>
      </P>

      <H2>Further reading</H2>
      <ul className="mt-4 space-y-3 list-none pl-0">
        <li>
          <Link
            to="/reactive-abuse-examples"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            Reactive abuse examples →
          </Link>
        </li>
        <li>
          <Link
            to="/darvo-examples"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            DARVO examples →
          </Link>
        </li>
        <li>
          <Link
            to="/why-do-i-feel-exhausted-after-conversations"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            Why do I feel exhausted after conversations →
          </Link>
        </li>
        <li>
          <Link
            to="/articulation-before-adjudication"
            className="text-[17px] md:text-[18px] text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
          >
            Articulation before adjudication →
          </Link>
        </li>
      </ul>

      <P>
        The pattern is already there. The question is whether you'll see it before it costs you
        — your peace, your version of events, your sense of yourself. This is{" "}
        <InlineLink to="/articulation-before-adjudication">
          the emerging category these tools belong to →
        </InlineLink>
      </P>

      <CTAButton />
    </SeoPageShell>
  );
};

export default WhyILookLikeTheCrazyOne;
